"use client";

import * as React from "react";
import {
  DecoratorNode,
  $createNodeSelection,
  $createParagraphNode,
  $getNodeByKey,
  $isParagraphNode,
  $setSelection,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalEditor,
  type NodeKey,
} from "lexical";
import { ImageBlockDecorator } from "../../interaction/floating/image/ImageBlockDecorator";
import {
  LEXICAL_IMAGE_BLOCK_TYPE,
  type ImageAlignment,
  type ImageCropRectNorm,
  type SerializedImageBlockNodeV1,
} from "./imageBlock.types";

export type { ImageAlignment, ImageCropRectNorm } from "./imageBlock.types";
export { LEXICAL_IMAGE_BLOCK_TYPE } from "./imageBlock.types";

function sanitizeCropNorm(c: ImageCropRectNorm): ImageCropRectNorm | null {
  const xx = Number(c.x);
  const yy = Number(c.y);
  const ww = Number(c.w);
  const hh = Number(c.h);
  if (![xx, yy, ww, hh].every((n) => Number.isFinite(n))) return null;
  let x = Math.min(1, Math.max(0, xx));
  let y = Math.min(1, Math.max(0, yy));
  let w = Math.min(1, Math.max(0.001, ww));
  let h = Math.min(1, Math.max(0.001, hh));
  if (x + w > 1) w = 1 - x;
  if (y + h > 1) h = 1 - y;
  if (w < 0.001 || h < 0.001) return null;
  if (x <= 1e-4 && y <= 1e-4 && w >= 1 - 1e-4 && h >= 1 - 1e-4) return null;
  return { x, y, w, h };
}

function parseCropNormAttr(raw: string | null): ImageCropRectNorm | null {
  if (!raw || !raw.trim()) return null;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    const x = Number(o.x);
    const y = Number(o.y);
    const w = Number(o.w);
    const h = Number(o.h);
    if (![x, y, w, h].every((n) => Number.isFinite(n))) return null;
    return sanitizeCropNorm({ x, y, w, h });
  } catch {
    return null;
  }
}

function parseImageAlignment(value: string | null): ImageAlignment {
  if (value === "left" || value === "right" || value === "center") return value;
  return "center";
}

function isDisallowedImageSrc(src: string): boolean {
  const s = src.trim().toLowerCase();
  return s.startsWith("data:") || s.startsWith("blob:");
}

/** 从粘贴 HTML（内部导出或外部带 img）还原图片块；不接受 data:/blob: 内联二进制 */
function $convertImageBlockElement(domNode: Node): DOMConversionOutput | null {
  if (!(domNode instanceof HTMLElement)) return null;
  let src = domNode.getAttribute("data-src") ?? "";
  let alt = domNode.getAttribute("data-alt") ?? "";
  const caption = domNode.getAttribute("data-caption") ?? "";
  const alignment = parseImageAlignment(domNode.getAttribute("data-alignment"));
  const wAttr = domNode.getAttribute("data-width-percent");
  let widthPercent: number | undefined;
  if (wAttr != null && wAttr !== "") {
    const n = Number(wAttr);
    if (Number.isFinite(n)) widthPercent = Math.min(100, Math.max(20, n));
  }
  const cropNorm = parseCropNormAttr(domNode.getAttribute("data-crop-norm"));
  if (!src) {
    const img = domNode.querySelector("img");
    if (img) {
      src = img.getAttribute("src") ?? "";
      alt = alt || (img.getAttribute("alt") ?? "");
    }
  }
  if (!src || isDisallowedImageSrc(src)) return null;
  return {
    node: $createImageBlockNode({
      src,
      alt,
      caption,
      alignment,
      openPickerOnMount: false,
      ...(widthPercent !== undefined ? { widthPercent } : {}),
      ...(cropNorm != null ? { cropNorm } : {}),
    }),
  };
}

export class ImageBlockNode extends DecoratorNode<React.ReactElement> {
  __src: string;
  __alt: string;
  __caption: string;
  __alignment: ImageAlignment;
  __openPickerOnMount: boolean;
  /** 相对内容区宽度 20–100，默认 50 */
  __widthPercent: number;
  /** 归一化裁切视窗；null 表示整图 */
  __cropNorm: ImageCropRectNorm | null;

  static getType(): string {
    return LEXICAL_IMAGE_BLOCK_TYPE;
  }

  static clone(node: ImageBlockNode): ImageBlockNode {
    return new ImageBlockNode(
      node.__src,
      node.__alt,
      node.__caption,
      node.__alignment,
      node.__openPickerOnMount,
      node.__widthPercent,
      node.__cropNorm,
      node.__key,
    );
  }

  constructor(
    src: string,
    alt: string,
    caption: string,
    alignment: ImageAlignment,
    openPickerOnMount: boolean,
    widthPercent: number,
    cropNorm: ImageCropRectNorm | null = null,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__alt = alt;
    this.__caption = caption;
    this.__alignment = alignment;
    this.__openPickerOnMount = openPickerOnMount;
    this.__widthPercent = widthPercent;
    this.__cropNorm = cropNorm;
  }

  isInline(): boolean {
    return false;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement("div");
    // min-height：占位条/加载前亦有可命中高度，配合矩形回退
    div.className = "rt-image-block w-full min-h-12";
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: Node) => {
        if (!(domNode instanceof HTMLElement)) return null;
        if (domNode.getAttribute("data-type") !== LEXICAL_IMAGE_BLOCK_TYPE) return null;
        return {
          conversion: $convertImageBlockElement,
          priority: 2,
        };
      },
    };
  }

  /** 写入 data-*，供 text/html 粘贴与跨编辑器降级路径还原 */
  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("div");
    element.className = "rt-image-block";
    element.setAttribute("data-type", LEXICAL_IMAGE_BLOCK_TYPE);
    element.setAttribute("data-src", this.__src);
    element.setAttribute("data-alt", this.__alt);
    element.setAttribute("data-caption", this.__caption);
    element.setAttribute("data-alignment", this.__alignment);
    element.setAttribute("data-width-percent", String(this.__widthPercent));
    if (this.__cropNorm != null) {
      element.setAttribute("data-crop-norm", JSON.stringify(this.__cropNorm));
    }
    return { element };
  }

  exportJSON(): SerializedImageBlockNodeV1 {
    const base: SerializedImageBlockNodeV1 = {
      type: LEXICAL_IMAGE_BLOCK_TYPE,
      version: 1,
      src: this.__src,
      alt: this.__alt,
      caption: this.__caption,
      alignment: this.__alignment,
      openPickerOnMount: this.__openPickerOnMount,
      widthPercent: this.__widthPercent,
    };
    if (this.__cropNorm != null) base.cropNorm = this.__cropNorm;
    return base;
  }

  static importJSON(serializedNode: SerializedImageBlockNodeV1): ImageBlockNode {
    let src = serializedNode.src ?? "";
    if (isDisallowedImageSrc(src)) src = "";
    const wp = serializedNode.widthPercent;
    const widthPercent =
      typeof wp === "number" && Number.isFinite(wp) ? Math.min(100, Math.max(20, wp)) : 50;
    const raw = serializedNode.cropNorm;
    let cropNorm: ImageCropRectNorm | null = null;
    if (raw && typeof raw === "object") {
      const o = raw as ImageCropRectNorm;
      cropNorm = sanitizeCropNorm({ x: o.x, y: o.y, w: o.w, h: o.h });
    }
    return new ImageBlockNode(
      src,
      serializedNode.alt,
      serializedNode.caption ?? "",
      serializedNode.alignment,
      false,
      widthPercent,
      cropNorm,
    );
  }

  setSrc(nextSrc: string) {
    const writable = this.getWritable();
    writable.__src = nextSrc;
    writable.__cropNorm = null;
  }

  setAlt(nextAlt: string) {
    const writable = this.getWritable();
    writable.__alt = nextAlt;
  }

  setCaption(next: string) {
    const writable = this.getWritable();
    writable.__caption = next;
  }

  setAlignment(next: ImageAlignment) {
    const writable = this.getWritable();
    writable.__alignment = next;
  }

  setOpenPickerOnMount(next: boolean) {
    const writable = this.getWritable();
    writable.__openPickerOnMount = next;
  }

  setWidthPercent(next: number) {
    const writable = this.getWritable();
    writable.__widthPercent = Math.min(100, Math.max(20, Math.round(next * 10) / 10));
  }

  setCropNorm(next: ImageCropRectNorm | null) {
    const writable = this.getWritable();
    writable.__cropNorm = next == null ? null : sanitizeCropNorm(next);
  }

  decorate(_editor: LexicalEditor) {
    return (
      <ImageBlockDecorator
        nodeKey={this.getKey()}
        src={this.__src}
        alt={this.__alt}
        caption={this.__caption}
        alignment={this.__alignment}
        openPickerOnMount={this.__openPickerOnMount}
        widthPercent={this.__widthPercent}
        cropNorm={this.__cropNorm}
      />
    );
  }
}

export function $createImageBlockNode({
  src = "",
  alt = "",
  caption = "",
  alignment = "center",
  openPickerOnMount = false,
  widthPercent = 50,
  cropNorm = null,
}: {
  src?: string;
  alt?: string;
  caption?: string;
  alignment?: ImageAlignment;
  openPickerOnMount?: boolean;
  widthPercent?: number;
  cropNorm?: ImageCropRectNorm | null;
} = {}) {
  const wp = Math.min(100, Math.max(20, widthPercent));
  const cn = cropNorm == null ? null : sanitizeCropNorm(cropNorm);
  return new ImageBlockNode(src, alt, caption, alignment, openPickerOnMount, wp, cn);
}

export function $isImageBlockNode(node: unknown): node is ImageBlockNode {
  return node instanceof ImageBlockNode;
}

/** 聚焦独立图片块（DecoratorNode 无 TS 上的 `select()`，用 NodeSelection） */
export function $focusImageBlockNode(node: ImageBlockNode): void {
  const sel = $createNodeSelection();
  sel.add(node.getKey());
  $setSelection(sel);
}

/**
 * 将当前段落替换为独立图片块，并在其后插入空段（便于继续输入）。
 * 须在 `editor.update()` 内调用。
 */
export function $replaceParagraphWithImageBlock(paragraphKey: string, openPickerOnMount = true): void {
  const node = $getNodeByKey(paragraphKey);
  if (!$isParagraphNode(node)) return;
  const img = $createImageBlockNode({ src: "", openPickerOnMount });
  node.replace(img);
  const tail = $createParagraphNode();
  img.insertAfter(tail);
  tail.selectStart();
}
